/**
 * The tab key is used to cycle between block types as quick, brainless way to
 * switch to the right one if you don't know the right command+digit key combo
 * or if you don't want to go up to the element picker menu.
 *
 * The rules that govern which elements to switch between depend on the script
 * format. Variety/classic scripts are context-free, but screenplays depend on
 * the previous block. Screenplays use this handler only for dual dialogue;
 * emptyBlockCycle handles their other empty blocks.
 *
 * Tab presses that change nothing fall through to later handlers, such as the
 * focus lock.
 */

import { type Command } from 'prosemirror-state'

import {
  NodeTypeKey,
  NodeTypeMap,
  ScriptDocType,
  ScriptFormatMap,
} from '@showrunner/codex'

import { getSelectionBlockType, nextNodeOnTab, setEditorBlockType } from '#util'

import { getLastBlockType } from '../../prose-utils.js'

/**
 * Build forward and reverse Tab commands for the script's format
 */
function getTabHandler(docType: ScriptDocType) {
  return (reverse = false): Command =>
    (viewState, dispatch) => {
      const { $from, $to, empty } = viewState.selection
      const dual = $from.depth > 2
      if (docType === ScriptFormatMap.SCREENPLAY && !(dual && empty)) {
        return false
      }
      // skip it if we're in the first node of a dual dialogue column
      // because it can only be character
      if (dual && $from.index(3) === 0) {
        return false
      }
      const blockType = getSelectionBlockType(viewState)
      // skip tabbing if this is a new act or end of act
      if (
        !blockType ||
        blockType === NodeTypeMap.NEW_ACT ||
        blockType === NodeTypeMap.END_OF_ACT
      ) {
        return false
      }
      const lastBlockType = getLastBlockType(viewState)
      const nextBlockType = nextNodeOnTab(
        blockType as NodeTypeKey,
        lastBlockType,
        docType,
        {
          reverse,
          dual,
        },
      )
      // dont cycle block type via tab when the selection spans several
      if (!nextBlockType || !$from.parent.eq($to.parent)) return false
      return setEditorBlockType(nextBlockType)(viewState, dispatch)
    }
}
export { getTabHandler }
